/** @jsxImportSource @emotion/react */
import { Outlet, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import * as authSelectors from "../auth/authSelectors";
import TimeslotsList from "./TimeslotsList";
import ShopUpsert from "./ShopUpsert";
import VenueShopNav from "../venue/VenueShopNav";
import { ShopProductsList } from "./ShopProductsList";
import { useGetSalesChannels, useGetShop } from "./service/shop-api";
import { SpinnerBlock } from "../common/Spinner";
import { SalesChannelsList } from "./SalesChannelsList";
import { useHandleFetchError } from "../../error-handler";
import { ShopPaymentProvidersList } from "./ShopPaymentProvidersList";
import { AutoAddedProducts } from "./AutoAddedProducts";

export const ShopSettings = () => {
  const params = useParams<{
    shopId: string | undefined;
  }>();

  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );
  const isSuperAdmin = useSelector(authSelectors.isSuperAdmin);
  const { data: shop, isLoading, error: shopError } = useGetShop(
    organizationKeyName,
    params.shopId
  );

  const {
    data: salesChannels,
    isLoading: salesChannelsLoading,
    error: salesChannelsError,
  } = useGetSalesChannels(organizationKeyName, params.shopId);

  useHandleFetchError(salesChannelsError);
  useHandleFetchError(shopError);

  if (isLoading || salesChannelsLoading || !salesChannels) {
    return <SpinnerBlock />;
  }

  const hasMigratedToMenus = salesChannels?.some(
    (sc) => sc.platform === "HeapsGo" && sc.menus.length > 0
  );

  return (
    <>
      <div className="container mt-7">
        <VenueShopNav
          organization={organizationKeyName}
          shopId={shop?.id}
          venueId={shop?.venueId}
        />
      </div>
      <ShopUpsert
        shopId={params.shopId}
        showOnlyBaseFields={true}
        redirectToProductsOnSave={true}
      />
      {isSuperAdmin && <ShopPaymentProvidersList />}
      <AutoAddedProducts
        organizationKeyName={organizationKeyName}
        shopId={params.shopId}
      />
      {shop && !hasMigratedToMenus && <ShopProductsList />}
      {shop && <SalesChannelsList shop={shop} salesChannels={salesChannels} />}
      {params.shopId && <TimeslotsList shopId={params.shopId} />}
      <Outlet />
    </>
  );
};
